import type {
  CustomerSummaryRecord,
  PaymentCollectionRecord,
  PaymentCurrency,
  PaymentDetailRecord,
  PaymentOrderRecord,
  PaymentOrderStatus
} from '@shared/types';

type StatusTone = 'neutral' | 'primary' | 'warning' | 'success' | 'info';
type SortDirection = 'asc' | 'desc';

export interface PaymentTableRowViewModel {
  id: string;
  title: string;
  customerName: string;
  statusLabel: string;
  statusTone: StatusTone;
  amountLabel: string;
  publicUrl: string;
  syncLabel: string;
  syncStatus: PaymentOrderRecord['syncStatus'];
  syncError: string | null;
  isPublicLinkReady: boolean;
  updatedLabel: string;
}

export interface PaymentEditorViewModel {
  title: string;
  customerName: string;
  projectName: string;
  contractLabel: string;
  invoiceLabel: string;
  issuerName: string;
  amountLabel: string;
  statusLabel: string;
  statusTone: StatusTone;
  syncLabel: string;
  expiresLabel: string;
  acceptedLabel: string;
  paidLabel: string;
}

function getCustomer(customers: CustomerSummaryRecord[], customerId: string) {
  return customers.find((customer) => customer.id === customerId) ?? null;
}

function statusTone(status: PaymentOrderStatus): StatusTone {
  switch (status) {
    case 'paid':
      return 'success';
    case 'payment_failed':
    case 'expired':
    case 'cancelled':
      return 'warning';
    case 'accepted':
    case 'checkout_started':
      return 'info';
    case 'sent':
    case 'viewed':
    case 'ready':
      return 'primary';
    default:
      return 'neutral';
  }
}

function formatShortDate(locale: string, value: string | null, fallback: string): string {
  if (!value) {
    return fallback;
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(new Date(value));
}

function formatMoney(locale: string, amountMinor: number, currency: PaymentCurrency): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 2
  }).format(amountMinor / 100);
}

function withDirection(value: number, direction: SortDirection): number {
  return direction === 'asc' ? value : value * -1;
}

function sortPaymentOrders(paymentOrders: PaymentOrderRecord[], sort: 'updatedAt' | 'amount' | 'status', direction: SortDirection) {
  const next = [...paymentOrders];

  switch (sort) {
    case 'amount':
      return next.sort((left, right) => withDirection(left.amountMinor - right.amountMinor, direction));
    case 'status':
      return next.sort((left, right) => withDirection(left.status.localeCompare(right.status), direction));
    default:
      return next.sort((left, right) => withDirection(new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime(), direction));
  }
}

export function selectPaymentsSummary(collection: PaymentCollectionRecord) {
  return {
    activeCount: collection.paymentOrders.filter((paymentOrder) => paymentOrder.status !== 'archived').length,
    archivedCount: collection.paymentOrders.filter((paymentOrder) => paymentOrder.status === 'archived').length
  };
}

export function selectPaymentRows(
  collection: PaymentCollectionRecord,
  options: {
    locale: string;
    statusFilter: 'all' | PaymentOrderStatus;
    customerFilter: 'all' | string;
    currencyFilter: 'all' | PaymentCurrency;
    sort: 'updatedAt' | 'amount' | 'status';
    sortDirection: SortDirection;
    t: (key: string) => string;
  }
): PaymentTableRowViewModel[] {
  return sortPaymentOrders(
    collection.paymentOrders.filter((paymentOrder) => {
      const matchesStatus = options.statusFilter === 'all' || paymentOrder.status === options.statusFilter;
      const matchesCustomer = options.customerFilter === 'all' || paymentOrder.customerId === options.customerFilter;
      const matchesCurrency = options.currencyFilter === 'all' || paymentOrder.currency === options.currencyFilter;
      return matchesStatus && matchesCustomer && matchesCurrency;
    }),
    options.sort,
    options.sortDirection
  ).map((paymentOrder) => {
    const customer = getCustomer(collection.customerSummaries, paymentOrder.customerId);

    return {
      id: paymentOrder.id,
      title: paymentOrder.title,
      customerName: customer?.name ?? paymentOrder.customerSnapshot.displayName,
      statusLabel: options.t(`enums.paymentStatus.${paymentOrder.status}`),
      statusTone: statusTone(paymentOrder.status),
      amountLabel: formatMoney(options.locale, paymentOrder.amountMinor, paymentOrder.currency),
      publicUrl: paymentOrder.publicUrl,
      syncLabel: options.t(`enums.paymentSyncStatus.${paymentOrder.syncStatus}`),
      syncStatus: paymentOrder.syncStatus,
      syncError: paymentOrder.syncError,
      isPublicLinkReady: paymentOrder.syncStatus === 'synced',
      updatedLabel: formatShortDate(options.locale, paymentOrder.updatedAt, options.t('common.notSet'))
    };
  });
}

export function selectPaymentEditorModel(
  detail: PaymentDetailRecord,
  options: {
    locale: string;
    t: (key: string) => string;
  }
): PaymentEditorViewModel {
  const paymentOrder = detail.paymentOrder;
  const customer = getCustomer(detail.customerSummaries, paymentOrder.customerId);
  const project = paymentOrder.projectId ? detail.projectSummaries.find((entry) => entry.id === paymentOrder.projectId) ?? null : null;
  const contract = paymentOrder.contractId
    ? detail.contractSummaries.find((entry) => entry.id === paymentOrder.contractId) ?? null
    : null;
  const invoice = paymentOrder.invoiceId ? detail.invoiceSummaries.find((entry) => entry.id === paymentOrder.invoiceId) ?? null : null;
  const issuer = detail.companyProfiles.find((entry) => entry.id === paymentOrder.issuerCompanyProfileId) ?? null;

  return {
    title: paymentOrder.title,
    customerName: customer?.name ?? paymentOrder.customerSnapshot.displayName,
    projectName: project?.name ?? options.t('common.notSet'),
    contractLabel: contract ? `${contract.contractNumber} - ${contract.title}` : options.t('common.notSet'),
    invoiceLabel: invoice?.label ?? options.t('common.notSet'),
    issuerName: issuer?.name ?? paymentOrder.issuerSnapshot.displayName,
    amountLabel: formatMoney(options.locale, paymentOrder.amountMinor, paymentOrder.currency),
    statusLabel: options.t(`enums.paymentStatus.${paymentOrder.status}`),
    statusTone: statusTone(paymentOrder.status),
    syncLabel: options.t(`enums.paymentSyncStatus.${paymentOrder.syncStatus}`),
    expiresLabel: formatShortDate(options.locale, paymentOrder.expiresAt, options.t('common.notSet')),
    acceptedLabel: formatShortDate(options.locale, paymentOrder.acceptedAt, options.t('common.notSet')),
    paidLabel: formatShortDate(options.locale, paymentOrder.paidAt, options.t('common.notSet'))
  };
}
